import React, { FC, ReactNode, useMemo, useEffect } from 'react';
import { ApolloError, useQuery } from '@apollo/client';

import useBackendConnectors from '../../hooks/useBackendConnectors';
import useBackendMetricsData from '../../hooks/useBackendMetricsData';
import { useMyMetricPermissions } from '../../hooks/useMyMetricPermissions';
import {
  GET_SPLITS,
  GET_DATABASE_METRICS_FOR_CUSTOM_REPORT,
  GET_DATABASE_SPLITS_FOR_CUSTOM_REPORT,
  GET_DB_CONNECTOR_CONFIG,
  GET_DATABASE_JOIN_GRAPH,
} from '../../utils/gql/queries';
import {
  Query,
  QueryGetSplitsArgs,
  ConnectorType,
  Split,
} from '../../utils/gql/types';
import { useSnackbar } from '../snackbar/hook/useSnackbar';

import { ConnectorsContext } from './context';
import { useAccount } from '../account/hook/useAccount';
import { ensureFidodbGeoSplits } from '../../components/DatabaseConnector/ensureFidodbGeoSplits';

export const ConnectorsProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const snackbar = useSnackbar();
  const { user } = useAccount();

  const {
    propertyId,
    analyticsAccountLoading,
    accountsLoading,
    clientAccountId,
    googleAdsConnectorId,
    shopifyConnectorId,
    facebookConnectorId,
    googleAnalytics4ConnectorId,
    googleSearchConsoleConnectorId,
    searchConsoleSiteUrl,
    databaseConnectorId,
    magentoConnectorId,
    ready: connectorsReady,
    shouldRedirect,
  } = useBackendConnectors();

  const {
    shopifyMetrics,
    magentoMetrics,
    facebookMetrics,
    googleAdsMetrics,
    googleAnalyticsMetrics,
    googleSearchConsoleMetrics,
    gettingFacebookMetrics,
    gettingShopifyMetrics,
    gettingMagentoMetrics,
    gettingGoogleAdsMetrics,
    gettingGoogleAnalyticsMetrics,
    gettingGoogleSearchConsoleMetrics,
    ready: metricsDataReady,
  } = useBackendMetricsData({
    facebookConnectorId,
    googleAdsConnectorId,
    shopifyConnectorId,
    googleAnalytics4ConnectorId,
    googleSearchConsoleConnectorId,
    magentoConnectorId,
    magentoConnectorEnabled: user?.magentoConnectorEnabled === true,
  });

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const { data: splitsData, error: splitsError } = useQuery<
    Pick<Query, 'getSplits'>,
    QueryGetSplitsArgs
  >(GET_SPLITS, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
    variables: {},
  });

  const splits = splitsData?.getSplits ?? [];

  // Carica splits del Database (aggiunti alla lista globale)
  const { data: dbSplitsData, error: dbSplitsError } = useQuery(
    GET_DATABASE_SPLITS_FOR_CUSTOM_REPORT,
    {
      variables: { connectorId: databaseConnectorId || '' },
      skip: !databaseConnectorId,
      fetchPolicy: 'network-only',
      notifyOnNetworkStatusChange: true,
    },
  );

  const dbSplits = useMemo((): Split[] => {
    const splits: Split[] = (
      dbSplitsData?.getDatabaseConnectorSplitsForCustomReport || []
    ).map((s: any) => ({
      id: s.key,
      connectorType: ConnectorType.DATABASE,
      key: s.key,
      label: s.name,
      valueType: 'string' as any,
    }));
    return ensureFidodbGeoSplits(splits);
  }, [dbSplitsData]);

  // Hook for current user metric permissions
  const { filterMetricsByPermissions, isMetricEnabled } =
    useMyMetricPermissions();

  // Filter metrics based on user permissions
  const filteredShopifyMetrics = useMemo(
    () => filterMetricsByPermissions(shopifyMetrics, 'shopify' as any),
    [shopifyMetrics, filterMetricsByPermissions],
  );

  const filteredFacebookMetrics = useMemo(
    () => filterMetricsByPermissions(facebookMetrics, 'facebook' as any),
    [facebookMetrics, filterMetricsByPermissions],
  );

  const filteredGoogleAdsMetrics = useMemo(
    () => filterMetricsByPermissions(googleAdsMetrics, 'googleAds' as any),
    [googleAdsMetrics, filterMetricsByPermissions],
  );

  const filteredGoogleAnalyticsMetrics = useMemo(
    () =>
      filterMetricsByPermissions(
        googleAnalyticsMetrics,
        'googleAnalytics4' as any,
      ),
    [googleAnalyticsMetrics, filterMetricsByPermissions],
  );

  const filteredGoogleSearchConsoleMetrics = useMemo(
    () =>
      filterMetricsByPermissions(
        googleSearchConsoleMetrics,
        'googleSearchConsole' as any,
      ),
    [googleSearchConsoleMetrics, filterMetricsByPermissions],
  );

  const filteredMagentoMetrics = useMemo(
    () => filterMetricsByPermissions(magentoMetrics, 'magento' as any),
    [magentoMetrics, filterMetricsByPermissions],
  );

  // Database metrics for Custom Report
  const {
    data: dbMetricsData,
    loading: gettingDatabaseMetrics,
    error: dbMetricsError,
  } = useQuery(GET_DATABASE_METRICS_FOR_CUSTOM_REPORT, {
    variables: { connectorId: databaseConnectorId || '' },
    skip: !databaseConnectorId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const { data: dbConfigData } = useQuery(GET_DB_CONNECTOR_CONFIG, {
    variables: { connectorId: databaseConnectorId || '' },
    skip: !databaseConnectorId,
    fetchPolicy: 'network-only',
  });

  const { data: dbJoinGraphData } = useQuery(GET_DATABASE_JOIN_GRAPH, {
    variables: { connectorId: databaseConnectorId || '' },
    skip: !databaseConnectorId,
    fetchPolicy: 'network-only',
  });

  const databaseMainTables = useMemo(() => {
    const selectedTables = dbConfigData?.getDatabaseConnectorConfig?.selectedTables || [];
    const fromConfig = selectedTables
      .map((entry: string) => {
        const parts = String(entry).split('.');
        return parts.length > 1 ? parts[1] : parts[0];
      })
      .filter(Boolean)
      .map((table: string) => table.toLowerCase());

    // Con catalogo Fidodb, le tabelle geo/utente devono essere candidate main anche se non in selectedTables.
    const hasFidodb = (dbSplits || []).some((split) =>
      String(split.key || '')
        .toLowerCase()
        .startsWith('fidodb.'),
    );
    if (!hasFidodb) return fromConfig;

    return Array.from(
      new Set([
        ...fromConfig,
        'utente',
        'payment',
        'com_dettagli_cliente',
        'anagrafica',
        'place',
        'abbonamento',
      ]),
    );
  }, [dbConfigData, dbSplits]);

  const databaseJoinEdges = useMemo(
    () => dbJoinGraphData?.getDatabaseConnectorJoinGraph || [],
    [dbJoinGraphData],
  );

  const databaseMetrics = useMemo(() => {
    const metrics = (
      dbMetricsData?.getDatabaseConnectorMetricsForCustomReport || []
    ).map((m: any) => ({
      key: m.key,
      label: m.name,
      category: 'metric',
      id: m.key,
      type: ConnectorType.DATABASE,
      valueType: 'number' as any,
    }));
    return metrics;
  }, [dbMetricsData]);

  // Combina tutti gli splits (globali + database) con deduplicazione
  const allSplits = useMemo(() => {
    const combinedSplits = [...splits, ...(dbSplits || [])];

    // Deduplicazione basata su key + connectorType
    const uniqueSplits = combinedSplits.reduce((acc: any[], current) => {
      const exists = acc.some(
        (split) =>
          split.key === current.key &&
          split.connectorType === current.connectorType,
      );
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, []);

    return uniqueSplits;
  }, [splits, dbSplits]);

  // Handle splits error
  useEffect(() => {
    if (splitsError) {
      onError(splitsError);
    }
  }, [splitsError, onError]);

  // Handle database splits error
  useEffect(() => {
    if (dbSplitsError) {
      onError(dbSplitsError);
    }
  }, [dbSplitsError, onError]);

  // Handle database metrics error
  useEffect(() => {
    if (dbMetricsError) {
      onError(dbMetricsError);
    }
  }, [dbMetricsError, onError]);

  return (
    <ConnectorsContext.Provider
      value={{
        shouldRedirect,
        ready: connectorsReady && metricsDataReady,
        shopifyMetrics: filteredShopifyMetrics,
        facebookMetrics: filteredFacebookMetrics,
        googleAdsMetrics: filteredGoogleAdsMetrics,
        googleAnalyticsMetrics: filteredGoogleAnalyticsMetrics,
        googleSearchConsoleMetrics: filteredGoogleSearchConsoleMetrics,
        magentoMetrics: filteredMagentoMetrics,
        databaseMetrics,
        gettingFacebookMetrics,
        gettingShopifyMetrics,
        gettingMagentoMetrics,
        gettingGoogleAdsMetrics,
        gettingGoogleAnalyticsMetrics,
        gettingGoogleSearchConsoleMetrics,
        gettingDatabaseMetrics,
        propertyId,
        searchConsoleSiteUrl,
        analyticsAccountLoading,
        accountsLoading,
        clientAccountId,
        googleAdsConnectorId,
        shopifyConnectorId,
        facebookConnectorId,
        googleAnalytics4ConnectorId,
        googleSearchConsoleConnectorId,
        databaseConnectorId,
        magentoConnectorId,
        magentoConnectorEnabled: user?.magentoConnectorEnabled === true,
        splits: allSplits,
        dbSplits: dbSplits || [],
        databaseJoinEdges,
        databaseMainTables,
        isMetricEnabled,
      }}
    >
      {children}
    </ConnectorsContext.Provider>
  );
};
